import { Component, OnInit } from '@angular/core';

import { MeetingRow, SolvedMeetingDto, Language, AvailabilitySlotDto, CategoryBase, MeetingDetailsDto } from '../shared/model';
import { MeetingsService } from './my-meetings-view.service';
import { SpinnerService } from '../shared/spinner/spinner.service';
import { SharedService } from '../shared/shared.service';
import { MainViewService } from '../main-view/main-view.service';
import { ConfirmationDeactivateService } from '../shared/ConfirmationDeactivateModule/confirmationdeactivate.service';
import { TranslatedNotificationService } from '../shared/translation/translated-notification.service';

import { formatDate } from '@telerik/kendo-intl';

import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-my-meetings-view',
  templateUrl: './my-meetings-view.component.html',
  styleUrls: ['./my-meetings-view.component.css',
    '../shared/styles/confirm-dialog.css',
    '../shared/styles/translation.css']
})
export class MyMeetingsViewComponent implements OnInit {

  public meetingData: Array<MeetingRow>;
  public pastMeetingData: Array<MeetingRow>;
  public cancelOpened = false;
  public rescheduleSlot = false;
  public currentLanguage: Language = <Language>{};
  public allLanguages: Array<Language>;
  public availabilitySlots: Array<AvailabilitySlotDto>;
  public rescheduleViewDirty = false;

  private selectedMeeting: MeetingRow = <MeetingRow>{};
  private solvedMeetingDto: SolvedMeetingDto = <SolvedMeetingDto>{};
  private flags;
  private translatedCategories: Array<CategoryBase>;
  private translatedLanguages: Array<Language>;
  private translatedMonths: {[key: string]: string};

  constructor(
    private meetingViewService: MeetingsService,
    private notificationService: TranslatedNotificationService,
    private spinnerService: SpinnerService,
    private sharedService: SharedService,
    private mainViewService: MainViewService,
    private confirmDeactivateService: ConfirmationDeactivateService) { }

  ngOnInit() {
    this.spinnerService.show('spinner');
    this.flags = this.sharedService.getFlags();
    this.sharedService.getLanguages().subscribe((result) => {
      this.allLanguages = result;
      forkJoin(
        this.sharedService.getLanguagesTranslations(),
        this.sharedService.getCategoryTranslations())
        .subscribe(() => { });
    },
      () => {
        this.notificationService
          .showMsgError('error.loading_language_definitions', 'Error while loading languages definitions.');
      });

    if (this.sharedService.areLanguagesTranslated()) {
      this.initializeData();
    } else {
      this.sharedService.getLanguagesTranslations()
      .subscribe(() => {
        this.initializeData();
      });
    }
  }

  initializeData() {
    this.translatedLanguages = this.sharedService.getTranslatedLanguages();
    this.currentLanguage = this.sharedService.translateLanguage(this.sharedService.getCurrentLanguage());
    this.translatedCategories = this.sharedService.getTranslatedCategories();
    this.loadMeetings();
  }

  loadMeetings() {
    const userEmail = this.sharedService.getLoggedUser().Email;

    forkJoin(
      this.meetingViewService.getUpcomingMeetings(userEmail),
      this.meetingViewService.getPastMeetings(userEmail),
      this.meetingViewService.getMonthsTranslations(this.currentLanguage.Id))
        .subscribe(([upcomingMeetings, pastMeetings, translations]) => {
          this.translatedMonths = translations;

          this.meetingData = upcomingMeetings.map(item => {
            const categoryTranslation = this.translatedCategories.find(t => t.Id === item.CategoryId);
            const languageTranslation = this.translatedLanguages.find(l => l.Id === item.LanguageId);

            return <MeetingRow>{
              Id: item.Id,
              CategoryName: this.getTranslatedCategoryName(item, categoryTranslation),
              CategoryId: item.CategoryId,
              LanguageName: this.getTranslatedLanguageName(item, languageTranslation),
              LanguageId: item.LanguageId,
              Time: this.getFormattedDate(item.Start, 'HH:mm') + ' - ' + this.getFormattedDate(item.End, 'HH:mm'),
              Date: this.getFormattedMonthTranslate(item.Start) + ' ' + this.getFormattedDate(item.Start, 'yyyy'),
              DayNumber: this.getFormattedDate(item.Start, 'dd'),
              Keyword: item.Keyword,
              MismatchedKeyword: item.MismatchedKeyword,
              ProblemDescription: item.ProblemDescription,
              AdditionalInformation: item.AdditionalInformation,
              Type: item.Type,
              PhoneNumber: item.PhoneNumber
            };
          });

          this.pastMeetingData = pastMeetings.map(item => {
            const categoryTranslation = this.translatedCategories.find(t => t.Id === item.CategoryId);
            const languageTranslation = this.translatedLanguages.find(l => l.Id === item.LanguageId);

            return <MeetingRow>{
              Id: item.Id,
              CategoryName: this.getTranslatedCategoryName(item, categoryTranslation),
              CategoryId: item.CategoryId,
              LanguageName: this.getTranslatedLanguageName(item, languageTranslation),
              LanguageId: item.LanguageId,
              Time: this.getFormattedDate(item.Start, 'HH:mm') + ' - ' + this.getFormattedDate(item.End, 'HH:mm'),
              Date: this.getFormattedMonthTranslate(item.Start) + ' ' + this.getFormattedDate(item.Start, 'yyyy'),
              DayNumber: this.getFormattedDate(item.Start, 'dd'),
              Status: item.Status,
              ProblemDescription: item.ProblemDescription
              };
          });
          this.spinnerService.hide('spinner');
        },
        (err) => {
          this.spinnerService.hide('spinner');
          this.notificationService.showMsgError('error.loading_meetings', 'Error while loading meetings');
        });
  }

  getSrcForLang(languageName: string): string {
    const language = this.translatedLanguages.find(l => l.Name === languageName);

    const flag = this.flags.find(x => x.code.toLowerCase() === language.LanguageCode.toString().toLowerCase());

    if (flag !== undefined) {
      return flag.src;
    }
    return '';
  }

  reschedule(meetingId: number) {
    this.selectedMeeting = this.meetingData.find(x => x.Id === meetingId );
    this.spinnerService.show('spinner');
    this.mainViewService.getAvailabilitySlots(this.selectedMeeting.CategoryId, this.currentLanguage.Id)
    .subscribe(result => {
      this.availabilitySlots = result;
      this.spinnerService.hide('spinner');
      this.rescheduleSlot = true;
    },
    () => {
      this.spinnerService.hide('spinner');
      this.notificationService.showMsgError('error.loading_slots', 'Error while loading availability slots');
    });
  }

  cancel(meetingId: number) {
    this.selectedMeeting.Id = meetingId;
    this.cancelOpened = true;
  }

  close() {
    this.cancelOpened = false;
    this.selectedMeeting = <MeetingRow>{};
  }

  cancelConfirmed() {
    this.spinnerService.show('spinner');
    this.meetingViewService.cancelMeeting(this.selectedMeeting.Id)
    .subscribe(() => {
      this.solvedMeetingDto.Id = this.selectedMeeting.Id;
      this.loadMeetings();
      this.selectedMeeting = <MeetingRow>{};
      this.cancelOpened = false;
      this.spinnerService.hide('spinner');
    },
    error => {
      this.cancelOpened = false;
      this.spinnerService.hide('spinner');
      this.selectedMeeting = <MeetingRow>{};
      this.notificationService
        .showMsgError('error.sending_cancel_email', 'Error while sending e-mail about cancelled meeting.');
    });
  }

  canDeactivate() {
    if (this.rescheduleViewDirty) {
      return this.confirmDeactivateService.show();
    }
    return true;
  }

  checkRescheduleViewDirty(dirty: boolean) {
    this.rescheduleViewDirty = dirty;
  }

  private getFormattedDate(time: any, format: string): string {
    const date = new Date(time + 'Z');
    return formatDate(date, format);
  }

  private getFormattedMonthTranslate(time: any): string {
    const date = new Date(time + 'Z');
    const month = formatDate(date, 'MMM');
    const monthNumber = formatDate(date, 'MM');
    const translatedMonth = !!this.translatedMonths['month.' + monthNumber] ? this.translatedMonths['month.' + monthNumber] : month;

    return translatedMonth;
  }

  private getTranslatedCategoryName(meeting: MeetingDetailsDto, translation: CategoryBase): string {
    if (!!translation && !!translation.Name && translation.Name.trim() !== '') {
      return translation.Name;
    }

    return meeting.CategoryName;
  }

  private getTranslatedLanguageName(meeting: MeetingDetailsDto, translation: Language): string {
    if (!!translation && !!translation.Name && translation.Name.trim() !== '') {
      return translation.Name;
    }

    return meeting.LanguageName;
  }

}
